import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import { FaCheck, FaArrowRight } from "react-icons/fa";

const ThankYou = () => {
  const history = useHistory();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const orderNumber = queryParams.get("order_number") || "---";
  const source = queryParams.get("source");
  const branch = queryParams.get("branch");

  const isExpress = source === "express";
  const backHref = isExpress && branch ? `/express/${branch}` : "/";
  const backLabel = isExpress ? "Back to Express" : "Back to products";

  return (
    <PageWrapper>
      <DispatchCard>
        <PerforatedEdge />

        <CardBody>
          <CardHeader>
            <Wordmark>MARKITI</Wordmark>
            <DocLabel>ORDER RECEIPT</DocLabel>
          </CardHeader>

          <StampWrapper>
            <Stamp>
              <FaCheck size={18} color="#00A89A" />
              <StampLabel>CONFIRMED</StampLabel>
            </Stamp>
          </StampWrapper>

          <Headline>Asante! Your order is confirmed</Headline>
          
          <OrderRow>
            <OrderLabel>Order no.</OrderLabel>
            <OrderCode>{orderNumber}</OrderCode>
          </OrderRow>

          <ContinueShoppingBtn onClick={() => history.push(backHref)}>
            <span>{backLabel}</span>
            <FaArrowRight size={13} />
          </ContinueShoppingBtn>
        </CardBody>

        <PerforatedEdge bottom />
      </DispatchCard>
    </PageWrapper>
  );
};

export default ThankYou;

const PageWrapper = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: #f4f1ea; /* Softened background */
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1.5rem;
  box-sizing: border-box;
`;

const DispatchCard = styled.div`
  background: #faf8f3;
  max-width: 380px; /* Slightly tightened width for better card proportions */
  width: 100%;
  border-radius: 6px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03); /* Soft modern shadow depth */
  box-sizing: border-box;
  overflow: hidden;
`;

const PerforatedEdge = styled.div`
  height: 14px;
  width: 100%;
  background-image: radial-gradient(
    circle at 10px ${props => (props.bottom ? "14px" : "0px")}, /* Flips cutout circles neatly on bottom edge */
    #f4f1ea 6px,
    transparent 6.5px
  );
  background-size: 20px 14px;
  background-position: 0 0;
`;

const CardBody = styled.div`
  padding: 1.5rem 2rem 2.5rem;
  font-family: "Inter", sans-serif;
  box-sizing: border-box;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2.5rem;
`;

const Wordmark = styled.span`
  font-family: "IBM Plex Mono", monospace;
  font-size: 11px;
  letter-spacing: 0.14em;
  color: #494395;
  font-weight: 600;
`;

const DocLabel = styled.span`
  font-family: "IBM Plex Mono", monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  color: #68639f; /* Darkened for better text contrast accessibility */
`;

const StampWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;
`;

const Stamp = styled.div`
  width: 86px;
  height: 86px;
  border-radius: 50%;
  border: 2px dashed rgba(0, 168, 154, 0.5); /* Softened dash alpha texture */
  display: flex;
  flex-direction: column; /* Stacks check icon over label cleanly */
  align-items: center;
  justify-content: center;
  gap: 4px;
  transform: rotate(-8deg);
  box-sizing: border-box;
  background: rgba(0, 168, 154, 0.02);
`;

const StampLabel = styled.div`
  font-family: "IBM Plex Mono", monospace;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.08em;
  color: #00a89a;
`;

const Headline = styled.h1`
  font-family: "Space Grotesk", sans-serif;
  font-size: 22px; /* Slightly reduced size for cleaner layout density */
  font-weight: 700;
  color: #2a2650;
  text-align: center;
  margin: 0 0 2.25rem;
  letter-spacing: -0.02em;
  line-height: 1.3;
`;

const OrderRow = styled.div`
  border-top: 1px dashed #dcd8cc;
  border-bottom: 1px dashed #dcd8cc;
  padding: 1.1rem 0;
  margin-bottom: 2.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const OrderLabel = styled.span`
  font-size: 12px;
  letter-spacing: 0.06em;
  color: #68639f; /* Better text contrast */
  font-weight: 500;
`;

const OrderCode = styled.span`
  font-family: "IBM Plex Mono", monospace;
  font-size: 14px;
  font-weight: 600;
  color: #494395;
  background: #e6f5f4; /* Muted aesthetic accent replace for the loud yellow */
  padding: 4px 10px;
  border-radius: 4px;
  word-break: break-all;
  letter-spacing: 0.02em;
`;

const ContinueShoppingBtn = styled.button`
  width: 100%;
  background: #494395;
  color: #ffffff;
  border: none;
  border-radius: 8px; /* Slightly rounder edges look cleaner and more premium */
  padding: 1rem;
  font-family: "Inter", sans-serif;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-sizing: border-box;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover {
    background: #38327a;
    box-shadow: 0 4px 12px rgba(73, 67, 149, 0.15); /* Lift animation on hover */
  }

  &:active {
    transform: scale(0.985);
  }
`;
